import { BaseTeam } from "@redotech/account-common/base-team";
import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { Card } from "@redotech/arbiter/card";
import { Flex } from "@redotech/arbiter/flex";
import { RedoIncrementDecrement } from "@redotech/arbiter/increment-decrement/redo-increment-decrement";
import { RedoTextareaInputField } from "@redotech/arbiter/input/redo-textarea-input-field";
import { RedoListItem } from "@redotech/arbiter/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/arbiter/select-dropdown/redo-single-select-dropdown-input";
import { Text } from "@redotech/arbiter/text";
import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import {
  CHARGE_SUBTYPE_TO_OUTBOUND_LABEL_TYPE,
  getTransactionTypeLabel,
} from "@redotech/redo-billing-common/balance/balance-transactions";
import { BalanceTypes } from "@redotech/redo-billing-common/balance/balances";
import { BillingChargeSubtype } from "@redotech/redo-billing-common/unified-billing/enums";
import { groupInput, input } from "@redotech/ui/form";
import { memo, useMemo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";

const ALLOWED_CHARGE_SUBTYPES: BillingChargeSubtype[] = [
  BillingChargeSubtype.ManualAdjustment,
  BillingChargeSubtype.ReduceBalance,
  BillingChargeSubtype.PromotionalCredit,
  BillingChargeSubtype.ChargeLabel,
  BillingChargeSubtype.SubscriptionFee,
  BillingChargeSubtype.WmsFee,
  BillingChargeSubtype.LandedCostFees,
  BillingChargeSubtype.RedoSupply,
];

function getChargeSubtypeLabel(subtype: BillingChargeSubtype): string {
  const transactionType = CHARGE_SUBTYPE_TO_OUTBOUND_LABEL_TYPE[subtype];
  return transactionType ? getTransactionTypeLabel(transactionType) : subtype;
}

export const creditForm = groupInput({
  amount: input<number>(),
  description: input<string>(),
});

export const AddBalanceCreditCard = memo(function BillingCard({
  team,
}: {
  team: BaseTeam | undefined;
}) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const [balance, setBalance] = useState<number | null>(null);
  const [chargeSubtype, setChargeSubtype] = useState<BillingChargeSubtype>(
    BillingChargeSubtype.ManualAdjustment,
  );
  const input = useInput(creditForm, { amount: 0, description: "" });

  const balanceLoad = useLoad(
    async (signal) => {
      if (!team) {
        return;
      }
      const balance = await rpcClient.getBalance(
        { account: BalanceTypes.OUTBOUND_LABELS, teamId: team._id },
        { signal },
      );
      if (balance?.balance) {
        setBalance(Number(balance.balance));
      }
    },
    [team],
  );

  const [addBalanceLoad, addBalance] = useTriggerLoad(async (signal) => {
    if (!team) {
      return;
    }
    const balance = await rpcClient.addBalanceCredit(
      {
        account: BalanceTypes.OUTBOUND_LABELS,
        amount: input.inputs.amount.value,
        teamId: team._id,
        chargeSubtype,
        description: input.inputs.description.value,
      },
      { signal },
    );

    input.inputs.amount.setValue(0);
    input.inputs.description.setValue("");
    if (balance?.balance) {
      setBalance(Number(balance.balance));
    }
  });

  const chargeSubtypeOptions: RedoListItem<BillingChargeSubtype>[] = useMemo(
    () =>
      ALLOWED_CHARGE_SUBTYPES.map((subtype) => ({
        id: subtype,
        type: "text" as const,
        text: getChargeSubtypeLabel(subtype),
        value: subtype,
      })),
    [],
  );

  return (
    <Card title="Add balance transaction">
      {balanceLoad.pending ? (
        <Text>Loading...</Text>
      ) : (
        <Flex flexDirection="column">
          <Flex pb="md">
            <Text>{`Current Balance: $${balance ?? "0.00"}`}</Text>
          </Flex>
          <Flex align="center" flexDirection="row">
            <Text>Amount</Text>
            <RedoIncrementDecrement
              placeholder="0"
              prefix="$"
              setValue={(value) => input.inputs.amount.setValue(value ?? 0)}
              size="sm"
              value={input.inputs.amount.value ?? 0}
            />
          </Flex>
          <Text fontSize="sm" textColor="error">
            A negative value will deduct from the balance, a positive value will
            add to the balance.
          </Text>
          <Flex align="center" flexDirection="row">
            <Text>Transaction Type:</Text>
            <RedoSingleSelectDropdownInput<BillingChargeSubtype>
              options={chargeSubtypeOptions}
              optionSelected={({ item }) => {
                setChargeSubtype(item.value);
              }}
              selectedItem={chargeSubtypeOptions.find(
                (o) => o.value === chargeSubtype,
              )}
              size="xs"
            />
          </Flex>
          <Flex flexDirection="column">
            <Text>Reason *</Text>
            <RedoTextareaInputField
              placeholder="Enter reason"
              setValue={(value: string) =>
                input.inputs.description.setValue(value)
              }
              value={input.inputs.description.value || ""}
            />
            <Text fontSize="sm" textColor="secondary">
              * Reason is required
            </Text>
          </Flex>
          <Flex>
            <RedoButton
              disabled={
                (input.inputs.amount.value ?? 0) === 0 ||
                !input.inputs.description.value?.trim()
              }
              hierarchy="primary"
              onClick={() => {
                addBalance();
              }}
              pending={addBalanceLoad.pending}
              size="sm"
              text="Add funds"
              theme="normal"
            />
          </Flex>
        </Flex>
      )}
    </Card>
  );
});
